import type { MinPriceRoom } from "@/types/services/hotel/apis/Detail";

// - - - - Skeleton - - - - //

import { differenceInDays, parse } from "date-fns-jalali";
import type { TFormatOptions } from "../components/common/counter/counter.types";
type TSize = "xs" | "sm" | "md" | "lg" | "xl" | "full" | TCustom;
type TCustom = {
  w: string;
  h: string;
};

type TSkeletonVariant = "round" | "circle";
interface SkeletonProps {
  variant?: TSkeletonVariant;
  size?: TSize;
  className?: string;
}

export const generateClassNames = ({
  variant,
  size,
  className,
}: SkeletonProps) => {
  const classes: string[] = [];

  if (size) sizeToClassName(size, classes);

  if (variant) variantToClassName(variant, classes);

  if (className) classes.push(className);

  return classes.join(" ");
};

function sizeToClassName(size: TSize, classes: string[]) {
  const sizes = {
    xs: "size-16",
    sm: "size-20",
    md: "size-24",
    lg: "size-32",
    xl: "size-40",
    full: "size-full",
    custom: `w-[${typeof size === "object" ? size.w : ""}] h-[${
      typeof size === "object" ? size.h : ""
    }]`,
  };

  classes.push(typeof size === "object" ? sizes.custom : sizes[size]);
}

function variantToClassName(variant: TSkeletonVariant, classes: string[]) {
  const variants = {
    circle: "rounded-full",
    round: "rounded-sm",
  };

  classes.push(variants[variant]);
}

// - - - - Counter - - - - //

export function formatCount(
  count: number,
  format: TFormatOptions | ((count: number) => string),
) {
  if (typeof format === "function") {
    return format(count);
  }

  const {
    prefix = "",
    suffix = "",
    thousandsSeparator,
    decimalPlaces,
  } = format;

  let formattedCount = count.toFixed(decimalPlaces || 0);
  if (thousandsSeparator) {
    formattedCount = formattedCount.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      thousandsSeparator,
    );
  }

  return `${prefix}${formattedCount} ${suffix}`;
}

// input format

type Room = {
  adult: number;
  child?: number[];
};

export const roomFormatter = (getValues: (field: string) => any) => {
  const rooms = getValues("rooms") ?? undefined;
  const totalRooms = rooms?.length;
  const totalPersons = rooms?.reduce(
    (pre: number, cur: Room) => pre + cur.adult + (cur.child?.length || 0),
    0,
  );

  return `${totalRooms} اتاق/${totalPersons} مسافر`;
};

export const formatDate = (getValues: (field: string) => any) => {
  const departDate = getValues("date.depart");
  const returnDate = getValues("date.return");

  return `${departDate ?? ""}${returnDate ? ` - ${returnDate}` : ""}`;
};

const stayDurationFormatter = (getValues: (field: string) => any) => {
  const startDate = parse(getValues("checkin") ?? "", "yyyy/MM/dd", new Date());
  const endDate = parse(getValues("checkout") ?? "", "yyyy/MM/dd", new Date());
  return `${differenceInDays(endDate, startDate)} شب`;
};

export const formatInputs = (
  type: string,
  getValues: (field: string) => any,
) => {
  switch (type) {
    case "rooms":
      return () => roomFormatter(getValues);
    case "checkout":
      return () => stayDurationFormatter(getValues);
    case "passengers":
      return () =>
        `${
          getValues("passengers").adult +
          getValues("passengers").child +
          getValues("passengers").infant
        } مسافر`;
    case "city":
      return () => getValues("city.name");
    case "origin":
      return () => getValues("origin.name");
    case "destination":
      return () => getValues("destination.name");
    case "date":
      return () => formatDate(getValues);
    default:
      return undefined;
  }
};

export function countExtraPassengers(
  rooms: MinPriceRoom[],
  roomId: string,
  extras: {
    adl: number;
    chd: number;
    inf: number;
  },
) {
  const room = rooms.find(
    ({ id, rate_plans }) => `${id}-${rate_plans.id}` === roomId,
  );

  const { adl, chd, inf } = extras;

  const { max_child_age, max_infant_age } =
    room?.rate_plans?.rules.child_rule ?? {};

  const { extra_bed_price, child_price } = room?.rate_plans ?? {};

  return {
    adult: {
      count: adl,
      max: 100,
      price: extra_bed_price,
      type: "adl",
    },
    child: {
      count: chd,
      max: max_child_age ?? null,
      price: child_price ?? 0,
      type: "chd",
    },
    infant: {
      count: inf,
      max: max_infant_age ?? null,
      price: 0,
      type: "inf",
    },
  };
}

export const toggleCheckBoxFilter = (
  checkbox: string,
  checked: boolean,
  currentState: string,
): string => {
  const filters = currentState ? currentState.split(",") : [];

  if (checked && !filters.includes(checkbox)) {
    filters.push(checkbox);
    return filters.join(",");
  }

  return filters.filter((item) => item !== checkbox).join(",");
};
